import { Box, Container, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  customerEditRequest,
  customerResetState,
  customerViewRequest,
} from "../../../redux/customer/CustomerAction";

import { StyledContainer, styles } from "../../components/formStyles";
import {
  handleFileChange,
  convertImageToBase64,
} from "../../constant/commonFileUpload";
import CustomButton from "../../constant/commonButton";
import ReusableFormFields from "../../constant/reusableFormFields";
import { APP_LINK } from "../../global/MenuItems";
import { mapBackendMessage } from "../../notification/BackendMessage";
import CommonDialog from "../../notification/Dialogbox";
import CommonSnackbar from "../../notification/Snackbar";
import uploadImage from "../../../image/wmremove-transformed-Photorooms.jpg";
import {
  customerCityRequest,
  customerCountryRequest,
  customerStateRequest,
} from "../../../redux/customerAddress/customerAddressAction";
import ImagePreviewDialog from "../../constant/commonImageDialog";

const EditCustomer = () => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    setError,
    watch,
    getValues,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editTriggered, setEditTriggered] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [isImageOnlySave, setIsImageOnlySave] = useState(false);

  const customerState = useSelector(
    (state) => state?.customer?.viewCustomer?.data || {}
  );
  const customerList = customerState || [];
  const editCustomerState = useSelector(
    (state) => state?.customer?.editCustomer
  );

  const countryState = useSelector(
    (state) => state?.customerAddress?.viewAllCountry?.data || {}
  );

  const countryList = countryState || [];

  const stateState = useSelector(
    (state) => state?.customerAddress?.viewAllState?.data || {}
  );

  const stateList = stateState || [];

  const cityState = useSelector(
    (state) => state?.customerAddress?.viewAllCity?.data || {}
  );

  const cityList = cityState || [];

  useEffect(() => {
    dispatch(customerCountryRequest());
  }, []);

  useEffect(() => {
    if (customerList?.country) {
      setSelectedCountry(customerList.country);
      setValue("country", customerList.country);

      dispatch(customerStateRequest(customerList.country));
    }
  }, [customerList, dispatch, setValue]);

  useEffect(() => {
    if (customerList?.state && stateList.length > 0) {
      setSelectedState(customerList.state);
      setValue("state", customerList.state);

      dispatch(customerCityRequest(customerList.state));
    }
  }, [customerList, stateList, dispatch, setValue]);

  useEffect(() => {
    if (customerList?.city && cityList.length > 0) {
      setValue("city", customerList.city);
    }
  }, [customerList, cityList, setValue]);

  const watchCountry = watch("country");
  const watchState = watch("state");

  useEffect(() => {
    if (watchCountry) {
      setSelectedCountry(watchCountry);
      setValue("state", "");
      setValue("city", "");
      setSelectedState(null);

      dispatch(customerStateRequest(watchCountry));
    }
  }, [watchCountry, dispatch, setValue]);

  useEffect(() => {
    if (watchState) {
      setSelectedState(watchState);
      setValue("city", "");

      dispatch(customerCityRequest(watchState));
    }
  }, [watchState, dispatch, setValue]);

  const inputFields = [
    {
      name: "reg_no",
      label: "Register No",
      readOnly: true,
    },
    {
      name: "first_name",
      label: "First Name",
    },
    {
      name: "last_name",
      label: "Last Name",
    },
    {
      name: "email",
      label: "Email",
      rules: {
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address",
        },
      },
    },
    {
      name: "contact",
      label: "Phone No",
      rules: {
        required: "Phone No is required",
        pattern: {
          value: /^\d{8,}$/,
          message: "Invalid Phone Number",
        },
      },
    },
    {
      name: "dob",
      label: "Date Of Birth",
      rules: {
        required: "Date of Birth is required",
        pattern: {
          value: /^\d{4}-\d{2}-\d{2}$/,
          message: "Invalid Date of Birth. Format: YYYY-MM-DD",
        },
      },
    },
    {
      name: "gender_type",
      label: "Gender",
      type: "select",
      options: [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
      ],
      defaultValue: customerList?.gender_type,
      rules: { required: "Gender is Required" },
    },
    {
      name: "address",
      label: "Address",
      size: "extraLarge",
      rules: { required: "Address is required" },
    },
    // {
    //   name: "street",
    //   label: "Street",
    // },
    {
      name: "country",
      label: "Country",
      type: "searchable-select",
      options: countryList.map((country) => ({
        value: country.id,
        label: country.country_name,
      })),
      onChange: (e) => setSelectedCountry(e.target.value),
      size: "selectMedium",
      rules: { required: "Country is required" },
    },
    {
      name: "state",
      label: "State",
      type: "searchable-select",
      options: stateList.map((state) => ({
        value: state.id,
        label: state.state_name,
      })),
      size: "selectMedium",
      rules: { required: "State is required" },
      disabled: !selectedCountry,
    },
    {
      name: "city",
      label: "City",
      type: "searchable-select",
      options: cityList.map((city) => ({
        value: city.id,
        label: city.city_name,
      })),
      size: "selectMedium",
      rules: { required: "City is required" },
      disabled: !selectedState,
    },
    {
      name: "pincode",
      label: "Pincode",
    },
    {
      name: "status",
      label: "status",
      type: "select",
      options: [
        { value: "1", label: "Active" },
        { value: "2", label: "InActive" },
      ],
      defaultValue: customerList?.status,
      rules: { required: "Status is Required" },
    },
  ];

  const handleImageUpload = (event) => {
    handleFileChange(
      event,
      setSelectedFile,
      setImagePreview,
      setFileName,
      showSnackbar
    );
  };

  useEffect(() => {
    const existingImageUrl = "https://example.com/image.jpg";
    convertImageToBase64(existingImageUrl, (base64) => {
      setSelectedFile(base64);
    });
  }, []);

  const handleCancel = () => {
    navigate(APP_LINK.CUSTOMERS);
  };

  useEffect(() => {
    if (id) {
      dispatch(customerViewRequest(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (customerList && customerList?.id === id) {
      setValue("reg_no", customerList?.reg_no);
      setValue("first_name", customerList?.first_name);
      setValue("last_name", customerList?.last_name);
      setValue("email", customerList?.email);
      setValue("contact", customerList?.phone);
      setValue("address", customerList?.address);
      setValue("gender_type", customerList?.gender_type);
      setValue("state", customerList?.state);
      setValue("city", customerList?.city);
      // setValue("street", customerList?.street);
      setValue("pincode", customerList?.pincode);
      setValue("country", customerList?.country);
      setValue("country_code", customerList?.country_code);

      setValue("dob", customerList?.dob);
      setValue("status", customerList?.status);
      const imageUrl = customerList?.prof_image;

      if (imageUrl) {
        if (imageUrl.startsWith("data:image")) {
          const base64Data = imageUrl.split(",")[1];
          setSelectedFile(base64Data);
          setImagePreview(imageUrl);
        } else {
          setImagePreview(imageUrl);

          convertImageToBase64(imageUrl).then((base64) => {
            if (base64) {
              setSelectedFile(base64);
            }
          });
        }
      } else {
        setSelectedFile(null);
        setImagePreview(uploadImage);
      }
    }
  }, [customerList, id, setValue]);

  const onSubmit = (data) => {
    const editRequestData = {
      ...data,
      id,
      prof_image: selectedFile || customerList?.prof_image,
    };
    setEditTriggered(true);
    dispatch(customerEditRequest(id, editRequestData));
  };

  const handleImage = () => {
    if (selectedFile || customerList?.prof_image) {
      const formData = getValues();

      const updatedData = {
        ...formData,
        id,
        prof_image: selectedFile || customerList?.prof_image,
      };
      setIsImageOnlySave(true);

      onSubmit(updatedData);
    } else {
      showSnackbar("Please choose a file before saving.", "error");
    }
  };

  const handleEditCustomerResponse = () => {
    if (editCustomerState?.error) {
      const errorFields = editCustomerState.errorFields || {};
      const message = editCustomerState.message || "";

      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });

        if (typeof message === "string" && message.trim()) {
          setDialogMessage(message);
          setIsSuccess(false);
          setDialogOpen(true);
        }
      } else if (typeof message === "string" && message.trim()) {
        const lowerMsg = message.toLowerCase();
        if (lowerMsg.includes("mobile")) {
          setError("contact", {
            type: "manual",
            message,
          });
        }
        if (lowerMsg.includes("email")) {
          setError("email", {
            type: "manual",
            message,
          });
        } else if (lowerMsg.includes("email")) {
          setError("email", {
            type: "manual",
            message,
          });
        } else {
          setDialogMessage(message);
          setIsSuccess(false);
          setDialogOpen(true);
        }
      } else {
        const customMessage = mapBackendMessage(
          editCustomerState.errormessage ||
            "An error occurred while updating the customer."
        );
        setDialogMessage(customMessage);
        setIsSuccess(false);
        setDialogOpen(true);
      }

      dispatch(customerResetState());
      dispatch(customerViewRequest(id));
    } else if (editCustomerState.success) {
      if (isImageOnlySave) {
        showSnackbar("Image saved successfully!", "success");
        dispatch(customerViewRequest(id));
      } else {
        setDialogMessage("Customer updated successfully");
        setIsSuccess(true);
        setDialogOpen(true);
        navigate(APP_LINK.CUSTOMERS);
      }
      dispatch(customerResetState());
      // dispatch(customerViewRequest(id));
      setIsImageOnlySave(false);
    }
  };

  useEffect(() => {
    if (editTriggered) {
      handleEditCustomerResponse();
    }
  }, [editTriggered, editCustomerState]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleImageClick = (image) => {
    if (image !== uploadImage) {
      setSelectedImage(image);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box>
              <Typography
                variant="h5"
                gutterBottom
                sx={styles.title}
                className="headingTable"
              >
                <h1>Update Customer </h1>
              </Typography>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Upload Profile Image
              </Typography>
            </Box>

            <Box>
              <StyledContainer>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                    <img
                      src={imagePreview || uploadImage}
                      alt="Profile Image"
                      width={80}
                      height={80}
                      style={styles.image}
                      onClick={() => handleImageClick(imagePreview)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Please upload square image, size less than 2MB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          onChange={handleImageUpload}
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(selectedFile)}
                        // disabled={!fileName}
                      >
                        {fileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={handleImage}
                        size="small"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={styles.note}>
                <span style={{ color: "red" }}>Note:</span> Only JPEG or PNG
                format is supported <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
          </Grid>
          {/* Form section */}
          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <ReusableFormFields
              inputFields={inputFields}
              control={control}
              errors={errors}
              register={register}
              setValue={setValue}
              required={false}
            />
            <Box sx={styles.submitGap}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={6}>
                  <CustomButton
                    text="Submit"
                    size="medium"
                    type="submit"
                    buttonStyle="StyledButton"
                    disabled={editCustomerState?.inProgress}
                    loading={editCustomerState?.inProgress}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomButton
                    text="Cancel"
                    variant="contained"
                    size="medium"
                    fullWidth
                    buttonStyle="style1"
                    onClick={handleCancel}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </form>
      <CommonSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
      <CommonDialog
        open={dialogOpen}
        isSuccess={isSuccess}
        onClose={handleDialogClose}
        redirectPath={APP_LINK.CUSTOMERS}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />
      {/* Popup Dialog */}
      <ImagePreviewDialog
        open={open}
        onClose={handleClose}
        selectedImage={selectedImage}
      />
    </Container>
  );
};
export default EditCustomer;
